import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot,
  Circle,
  Send,
  ChevronDown,
  Trash2,
} from 'lucide-react';
import { publicApi, stackApi } from '../utils/api';
import ConversationChips from '../components/ConversationChips';
import ConsultingTriggerCard from '../components/ConsultingTriggerCard';
import { FindAgent } from '../services/FindAgent';
import { useAuth } from '../contexts/AuthContext';
import { loadBusinessContext } from '../utils/businessContext';
import { mergeGuestStackTools, parseGuestStack, removeGuestStackTool, saveGuestStack } from '../utils/runningStackStore';

const STAGE_NAMES = ['Discovery', 'Context', 'Matching', 'Recommend', 'Implement'];
const FIND_NEEDLE_KEY = 'wowrolla_findpage_needle_ids';
const FIND_RECOMMENDED_KEY = 'wowrolla_findpage_recommended_ids';

const getFinderPageStorage = (isAuthenticated = false) => {
  if (typeof window === 'undefined') return null;
  try {
    return isAuthenticated ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

function TypingDots() {
  return (
    <div className="inline-flex items-center gap-1" aria-label="Wowrolla is thinking" data-testid="finder-typing-indicator">
      {[0, 1, 2].map((idx) => (
        <motion.span
          key={idx}
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: 'var(--text-secondary)' }}
          animate={{ y: [0, -3, 0], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 0.6, repeat: Infinity, delay: idx * 0.15 }}
        />
      ))}
    </div>
  );
}

function parseQuery(search) {
  const params = new URLSearchParams(search);
  const resultRaw = params.get('result') || '';
  return {
    from: params.get('from') || '',
    tools: (params.get('tools') || '').split(',').map((s) => s.trim()).filter(Boolean),
    pain: params.get('pain') || '',
    role: params.get('role') || '',
    profile: params.get('profile') || '',
    budget: params.get('budget') || '',
    result: resultRaw,
    resultTokens: resultRaw.split('-').map((s) => s.trim()).filter(Boolean),
    tool: params.get('tool') || '',
  };
}

function normalizeLabel(value) {
  return (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function profileTagEntries(context) {
  const tags = [];
  if (context.role) tags.push({ key: 'role', value: context.role });
  if (context.teamSize) tags.push({ key: 'team', value: context.teamSize });
  if (context.budget) tags.push({ key: 'budget', value: context.budget });
  return tags;
}

const WORKFLOW_COLOR_PALETTE = [
  { bg: 'rgba(255,232,0,0.08)', border: 'rgba(255,232,0,0.28)', text: '#FDE047' },
  { bg: 'rgba(57,255,20,0.08)', border: 'rgba(57,255,20,0.28)', text: '#86EFAC' },
  { bg: 'rgba(56,189,248,0.08)', border: 'rgba(56,189,248,0.28)', text: '#7DD3FC' },
  { bg: 'rgba(196,181,253,0.10)', border: 'rgba(196,181,253,0.30)', text: '#DDD6FE' },
  { bg: 'rgba(251,146,60,0.10)', border: 'rgba(251,146,60,0.30)', text: '#FDBA74' },
];

const ACTION_COLOR_PALETTE = [
  { bg: 'rgba(255,232,0,0.16)', border: 'rgba(255,232,0,0.52)', text: '#FFE800' },
  { bg: 'rgba(57,255,20,0.16)', border: 'rgba(57,255,20,0.52)', text: '#39FF14' },
  { bg: 'rgba(56,189,248,0.16)', border: 'rgba(56,189,248,0.52)', text: '#38BDF8' },
  { bg: 'rgba(244,114,182,0.16)', border: 'rgba(244,114,182,0.52)', text: '#F472B6' },
  { bg: 'rgba(167,139,250,0.16)', border: 'rgba(167,139,250,0.52)', text: '#A78BFA' },
];

function decorateAttentionChips(chips = []) {
  return chips.map((chip, idx) => {
    const label = (chip?.label || chip || '').toString().toLowerCase();
    const isAction = /add|compare|book|start|find more|yes|tell me|myself|save|buy|consult/.test(label);
    const palette = isAction ? ACTION_COLOR_PALETTE : WORKFLOW_COLOR_PALETTE;
    const color = palette[idx % palette.length];
    return {
      ...chip,
      type: 'workflow',
      bgColor: color.bg,
      borderColor: color.border,
      textColor: color.text,
    };
  });
}

function dedupeStackItems(items = []) {
  const seen = new Set();
  return (items || []).filter((item) => {
    const id = item?.tool_id || item?.id || item?.toolId;
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

function derivePainPointFromBusinessContext(businessContext) {
  if (Array.isArray(businessContext?.selectedWorkflows) && businessContext.selectedWorkflows.length) {
    return businessContext.selectedWorkflows.slice(0, 3).join(', ');
  }
  if (businessContext?.businessType) {
    return `${businessContext.industry} workflows`;
  }
  return '';
}

export default function FindPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const query = useMemo(() => parseQuery(location.search), [location.search]);
  const businessContext = useMemo(() => loadBusinessContext(isAuthenticated), [isAuthenticated]);
  const businessPrefill = useMemo(() => ({
    painPoint: derivePainPointFromBusinessContext(businessContext),
    role: businessContext?.businessType || '',
    teamSize: businessContext?.teamSize || '',
  }), [businessContext]);
  const isBuildRoute = location.pathname === '/my-stack/build';

  const [stage, setStage] = useState(0);
  const [context, setContext] = useState({ needle: [] });
  const [messages, setMessages] = useState([]);
  const [chips, setChips] = useState([]);
  const [tools, setTools] = useState([]);
  const [needle, setNeedle] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId] = useState(() => (globalThis.crypto?.randomUUID?.() || `session-${Date.now()}`));
  const [allTools, setAllTools] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [consultingScore, setConsultingScore] = useState(0);
  const [showConsulting, setShowConsulting] = useState(false);
  const [inputText, setInputText] = useState('');
  const [chipSelections, setChipSelections] = useState([]);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [isMobileContextOpen, setIsMobileContextOpen] = useState(false);
  const [inputHint, setInputHint] = useState('');
  const [dashboardStack, setDashboardStack] = useState([]);

  const agentRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messageListRef = useRef(null);
  const textareaRef = useRef(null);

  const liveToolCount = allTools.length;
  const suggestedCount = tools.length;

  const loadDashboardStack = useCallback(async () => {
    try {
      if (isAuthenticated) {
        const response = await stackApi.getDashboardStack();
        const serverItems = dedupeStackItems(response.data?.items || []);

        const guestItems = dedupeStackItems(parseGuestStack());
        if (guestItems.length > 0) {
          const existing = new Set(
            serverItems
              .map((item) => item.tool_id || item.id || item.toolId)
              .filter(Boolean)
          );

          for (const item of guestItems) {
            const toolId = item.tool_id || item.id || item.toolId;
            if (!toolId || existing.has(toolId)) continue;
            try {
              await stackApi.addStackItem({
                tool_id: toolId,
                notes: item.notes || '',
                monthly_cost: Number(item.monthly_cost || 0),
                annual_renewal_date: item.annual_renewal_date || null,
              });
              existing.add(toolId);
            } catch {
              // Continue syncing remaining tools when one fails.
            }
          }

          saveGuestStack([]);
          const refreshed = await stackApi.getDashboardStack();
          setDashboardStack(dedupeStackItems(refreshed.data?.items || []));
          return;
        }

        setDashboardStack(serverItems);
      } else {
        setDashboardStack(dedupeStackItems(parseGuestStack()));
      }
    } catch {
      // Authenticated users should only reflect server state to stay in sync with My Tool Dashboard.
      setDashboardStack(isAuthenticated ? [] : dedupeStackItems(parseGuestStack()));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!messageListRef.current) return;
    messageListRef.current.scrollTo({ top: messageListRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const loadTools = async () => {
      try {
        const [toolsRes, categoriesRes] = await Promise.all([
          publicApi.getAllTools(false),
          publicApi.getCategories(),
        ]);
        setAllTools(toolsRes.data || []);
        setAllCategories(categoriesRes.data || []);
      } catch {
        setAllTools([]);
        setAllCategories([]);
      }
    };
    loadTools();
  }, []);

  useEffect(() => {
    loadDashboardStack();
    const onStorage = () => loadDashboardStack();
    const onStackUpdated = () => loadDashboardStack();
    window.addEventListener('storage', onStorage);
    window.addEventListener('wowrolla:stack-updated', onStackUpdated);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('wowrolla:stack-updated', onStackUpdated);
    };
  }, [loadDashboardStack]);

  useEffect(() => {
    if (!allTools.length) return;

    const onMessage = (msg) => {
      setMessages((prev) => [...prev, msg]);
      setIsTyping(false);
    };

    const onChips = (nextChips) => {
      const base = nextChips || [];
      setChips(base);
      setChipSelections([]);
    };

    const onTools = (nextTools) => {
      setTools((nextTools || []).slice(0, 6));
    };

    const onStageChange = (nextStage) => {
      setStage(nextStage);
      window.dispatchEvent(new CustomEvent('wowrolla-ga4', { detail: { event: 'find_stage_completed', stage_name: STAGE_NAMES[nextStage] } }));
    };

    const onConsultingTrigger = (score, ctx) => {
      setConsultingScore(score || 0);
      setShowConsulting(true);
      setContext({ ...ctx });
      window.dispatchEvent(new CustomEvent('wowrolla-ga4', { detail: { event: 'find_consulting_shown', complexity_score: score || 0 } }));
    };

    const agent = new FindAgent(
      allTools,
      onMessage,
      onChips,
      onTools,
      onStageChange,
      onConsultingTrigger,
      (ctx) => setContext({ ...ctx }),
      {
        sessionId,
        persistentStorage: isAuthenticated,
        liveStats: {
          toolCount: allTools.length,
          categoryCount: allCategories.length,
        },
        seedContext: {
          painPoint: query.pain || businessPrefill.painPoint || undefined,
          role: query.role || businessPrefill.role || undefined,
          teamSize: businessPrefill.teamSize || undefined,
        },
        onReset: () => {
          setNeedle([]);
          setTools([]);
          setMessages([]);
          setShowConsulting(false);
          setConsultingScore(0);
          setContext({ needle: [] });
          setChipSelections([]);
          getFinderPageStorage(isAuthenticated)?.removeItem(FIND_NEEDLE_KEY);
          getFinderPageStorage(isAuthenticated)?.removeItem(FIND_RECOMMENDED_KEY);
        },
      }
    );

    agentRef.current = agent;

    const restored = FindAgent.restoreFromStorage({ persistentStorage: isAuthenticated });
    const hasRecommendationSnapshot = Boolean(getFinderPageStorage(isAuthenticated)?.getItem(FIND_RECOMMENDED_KEY));
    if (restored && hasRecommendationSnapshot) {
      // Seamless return path: keep recommendations visible when user navigates away and comes back.
      agent.hydrate(restored);
      const replayed = (restored.history || []).map((item, idx) => ({
        id: `restore-${idx}-${Date.now()}`,
        role: item.role,
        content: item.content,
        createdAt: new Date().toISOString(),
      }));
      setMessages(replayed);
      setContext(restored.context || { needle: [] });
      const restoredNeedleIds = restored.context?.needle || [];
      const pre = allTools.filter((tool) => restoredNeedleIds.includes(tool.id));
      setNeedle(pre);
    } else if (restored) {
      setShowRestorePrompt(true);
    } else {
      agent.start();
      agent.applyPrefill({
        painPoint: query.pain || businessPrefill.painPoint,
        role: query.role || businessPrefill.role,
        budget: query.budget,
      });
      window.dispatchEvent(new CustomEvent('wowrolla-ga4', { detail: { event: 'find_started' } }));
    }
  }, [allTools, allCategories, query.pain, query.role, query.budget, sessionId, isAuthenticated, businessPrefill.painPoint, businessPrefill.role, businessPrefill.teamSize]);

  useEffect(() => {
    if (!allTools.length || !agentRef.current) return;
    if (!query.tools.length && !query.from && !query.pain && !query.role && !query.profile && !isBuildRoute) return;

    const seeded = {};
    const toolKeys = new Set([...query.tools, ...query.resultTokens].map((entry) => normalizeLabel(entry)));
    const preselected = allTools.filter((tool) => {
      const slugKey = normalizeLabel(tool.slug);
      const nameKey = normalizeLabel(tool.name);
      return toolKeys.has(slugKey) || toolKeys.has(nameKey);
    }).slice(0, 3);

    if (preselected.length) {
      setNeedle(preselected);
      setContext((prev) => ({ ...prev, needle: preselected.map((t) => t.id) }));
    }

    if (query.pain) seeded.painPoint = query.pain;
    if (query.role) seeded.role = query.role;
    if (query.budget) seeded.budget = query.budget;

    if (query.from === 'stack-builder' || isBuildRoute) {
      setMessages((prev) => [...prev, { id: `seed-${Date.now()}`, role: 'assistant', content: "I see you're building a stack - let me help refine it.", createdAt: new Date().toISOString() }]);
    }
    if (query.from === 'haystack') {
      setMessages((prev) => [...prev, { id: `seed-${Date.now()}-2`, role: 'assistant', content: 'Good choices so far - let me check they work well together.', createdAt: new Date().toISOString() }]);
    }
    if (query.from === 'compare') {
      setMessages((prev) => [...prev, { id: `seed-${Date.now()}-c`, role: 'assistant', content: 'I can refine this comparison and tell you what to start with first.', createdAt: new Date().toISOString() }]);
    }
    if (query.from === 'alternatives' && query.tool) {
      setMessages((prev) => [...prev, { id: `seed-${Date.now()}-3`, role: 'assistant', content: `Looking to switch from ${query.tool}? I can shortlist a better-fit alternative.`, createdAt: new Date().toISOString() }]);
    }

    setContext((prev) => ({ ...prev, ...seeded, needle: preselected.map((t) => t.id) }));
  }, [allTools, query, isBuildRoute]);

  useEffect(() => {
    try {
      const raw = getFinderPageStorage(isAuthenticated)?.getItem(FIND_NEEDLE_KEY);
      if (!raw || !allTools.length || needle.length > 0) return;
      const ids = JSON.parse(raw);
      if (!Array.isArray(ids) || !ids.length) return;
      const restored = allTools.filter((tool) => ids.includes(tool.id));
      if (restored.length) {
        setNeedle(restored);
        setContext((prev) => ({ ...prev, needle: restored.map((tool) => tool.id) }));
      }
    } catch {
      // no-op
    }
  }, [allTools, needle.length, isAuthenticated]);

  // Keep recommendation cards durable when user opens a tool details page and returns.
  useEffect(() => {
    try {
      const raw = getFinderPageStorage(isAuthenticated)?.getItem(FIND_RECOMMENDED_KEY);
      if (!raw || !allTools.length || tools.length > 0) return;
      const ids = JSON.parse(raw);
      if (!Array.isArray(ids) || !ids.length) return;
      const restored = allTools.filter((tool) => ids.includes(tool.id)).slice(0, 6);
      if (restored.length) {
        setTools(restored);
      }
    } catch {
      // no-op
    }
  }, [allTools, tools.length, isAuthenticated]);

  useEffect(() => {
    if (!needle.length) return;
    getFinderPageStorage(isAuthenticated)?.setItem(FIND_NEEDLE_KEY, JSON.stringify(needle.map((item) => item.id).filter(Boolean)));
  }, [needle, isAuthenticated]);

  useEffect(() => {
    if (!tools.length) return;
    getFinderPageStorage(isAuthenticated)?.setItem(FIND_RECOMMENDED_KEY, JSON.stringify(tools.map((item) => item.id).filter(Boolean).slice(0, 6)));
  }, [tools, isAuthenticated]);

  useEffect(() => {
    if (needle.length > 0 || !dashboardStack.length || !allTools.length) return;
    const ids = dashboardStack
      .map((item) => item.tool_id || item.id || item.toolId)
      .filter(Boolean);
    if (!ids.length) return;
    const hydrated = allTools.filter((tool) => ids.includes(tool.id));
    if (hydrated.length) {
      setNeedle(hydrated);
      setContext((prev) => ({ ...prev, needle: hydrated.map((tool) => tool.id) }));
    }
  }, [dashboardStack, allTools, needle.length]);

  const submitInput = async (rawText) => {
    const text = (rawText || '').trim();
    if (!agentRef.current || isTyping) return;

    if (context.sessionLimitReached) {
      setInputHint('Session question limit reached (20). Start over or book a consulting call to continue.');
      textareaRef.current?.focus();
      return;
    }

    if (!text) {
      setInputHint('Type a short answer or pick at least one workflow before sending.');
      textareaRef.current?.focus();
      return;
    }

    setInputHint('');
    setIsTyping(true);
    await agentRef.current.handleUserInput(text);
    setInputText('');
  };

  const addToNeedle = async (tool) => {
    if (!tools.length) {
      setMessages((prev) => [...prev, {
        id: `guard-${Date.now()}`,
        role: 'assistant',
        content: 'I can only add tools after I have suggested at least one result. Answer one more question first.',
        createdAt: new Date().toISOString(),
      }]);
      return;
    }

    if (!tools.some((item) => item.id === tool.id)) {
      setMessages((prev) => [...prev, {
        id: `guard-not-suggested-${Date.now()}`,
        role: 'assistant',
        content: 'That tool is not in the current suggestions. Please add from the suggested list.',
        createdAt: new Date().toISOString(),
      }]);
      return;
    }

    const existingIds = new Set([
      ...needle.map((item) => item.id || item.tool_id || item.toolId),
      ...dashboardStack.map((item) => item.id || item.tool_id || item.toolId),
    ].filter(Boolean));

    if (existingIds.has(tool.id)) {
      return;
    }

    setNeedle((prev) => {
      if (prev.some((t) => t.id === tool.id)) return prev;
      const next = [...prev, tool];
      setContext((ctx) => ({ ...ctx, needle: next.map((i) => i.id) }));
      return next;
    });

    window.dispatchEvent(new CustomEvent('wowrolla-ga4', { detail: { event: 'find_tool_added', tool_name: tool.name, stack_size: needle.length + 1 } }));

    try {
      if (isAuthenticated) {
        await stackApi.addStackItem({ tool_id: tool.id, notes: '' });
      } else {
        const current = parseGuestStack();
        const existingIds = new Set(current.map((item) => item.tool_id || item.id || item.toolId).filter(Boolean));
        if (!existingIds.has(tool.id)) {
          const next = mergeGuestStackTools(current, [{
            tool_id: tool.id,
            name: tool.name,
            slug: tool.slug,
            category_name: tool.category_name || '',
            monthly_cost: Number(tool.monthly_cost || 0),
            notes: '',
          }]);
          saveGuestStack(next);
        }
      }
      window.dispatchEvent(new Event('wowrolla:stack-updated'));
    } catch {
      // no-op, local state is source of truth for UX
    }
  };

  const removeFromPinnedDashboard = async (toolId) => {
    if (!toolId) return;

    if (isAuthenticated) {
      try {
        await stackApi.removeStackItem(toolId);
      } catch {
        return;
      }
    } else {
      const next = removeGuestStackTool(parseGuestStack(), toolId);
      saveGuestStack(next);
    }

    setNeedle((prev) => prev.filter((item) => (item.id || item.tool_id || item.toolId) !== toolId));
    window.dispatchEvent(new Event('wowrolla:stack-updated'));
    await loadDashboardStack();
  };

  const clearPinnedDashboard = async () => {
    const ids = dedupeStackItems(dashboardStack)
      .map((item) => item.tool_id || item.id || item.toolId)
      .filter(Boolean);

    if (!ids.length) {
      return;
    }

    if (isAuthenticated) {
      await Promise.allSettled(ids.map((id) => stackApi.removeStackItem(id)));
    } else {
      saveGuestStack([]);
    }

    setNeedle([]);
    setContext((ctx) => ({ ...ctx, needle: [] }));
    window.dispatchEvent(new Event('wowrolla:stack-updated'));
    await loadDashboardStack();
  };

  const handleChipSelect = async (chip) => {
    if (!chip?.label) return;

    if (stage === 0) {
      const selected = chipSelections.includes(chip.label);
      if (selected) {
        setChipSelections((prev) => prev.filter((item) => item !== chip.label));
      } else if (chipSelections.length < 3) {
        setChipSelections((prev) => [...prev, chip.label]);
      }
      return;
    }

    if (chip.label === 'Compare these') {
      const ids = (needle.length ? needle : tools).slice(0, 3).map((t) => t.id);
      if (ids.length >= 2) navigate(`/compare?tools=${ids.join(',')}`);
      return;
    }

    if (chip.label === 'Book a call' || chip.label === 'Yes, book a call') {
      window.dispatchEvent(new CustomEvent('wowrolla-ga4', { detail: { event: 'find_consulting_booked' } }));
      navigate(`/consulting?from=finder&complexity=${consultingScore}`);
      return;
    }

    if (chip.label === "I'll set it up myself") {
      setShowConsulting(false);
      setMessages((prev) => [...prev, { id: `self-${Date.now()}`, role: 'assistant', content: "No problem - here's your setup guide for each tool in your stack.", createdAt: new Date().toISOString() }]);
      window.dispatchEvent(new CustomEvent('wowrolla-ga4', { detail: { event: 'find_consulting_skipped' } }));
      return;
    }

    await submitInput(chip.label);
  };

  const handleRestoreYes = async () => {
    setShowRestorePrompt(false);
    const restored = FindAgent.restoreFromStorage({ persistentStorage: isAuthenticated });
    if (!restored || !agentRef.current) {
      await agentRef.current?.start();
      return;
    }

    await agentRef.current.hydrate(restored);
    const replayed = (restored.history || []).map((item, idx) => ({
      id: `restore-${idx}-${Date.now()}`,
      role: item.role,
      content: item.content,
      createdAt: new Date().toISOString(),
    }));
    setMessages(replayed);
    setContext(restored.context || { needle: [] });

    const restoredNeedleIds = (restored.context?.needle || []);
    const pre = allTools.filter((tool) => restoredNeedleIds.includes(tool.id));
    setNeedle(pre);
  };

  const handleRestoreNo = async () => {
    setShowRestorePrompt(false);
    FindAgent.clearStorage({ persistentStorage: isAuthenticated });
    getFinderPageStorage(isAuthenticated)?.removeItem(FIND_NEEDLE_KEY);
    getFinderPageStorage(isAuthenticated)?.removeItem(FIND_RECOMMENDED_KEY);
    setMessages([]);
    setTools([]);
    setNeedle([]);
    setContext({ needle: [] });
    if (agentRef.current) {
      await agentRef.current.start();
    }
  };

  const messageRows = messages;
  const visibleChips = decorateAttentionChips(chips);
  const stackPreviewItems = useMemo(() => {
    if (isAuthenticated) {
      return dedupeStackItems(dashboardStack);
    }
    const guestSource = dashboardStack.length > 0 ? dashboardStack : needle;
    return dedupeStackItems(guestSource);
  }, [dashboardStack, needle, isAuthenticated]);
  const stackToolIds = useMemo(
    () => new Set([
      ...needle.map((item) => item.id || item.tool_id || item.toolId),
      ...dashboardStack.map((item) => item.id || item.tool_id || item.toolId),
    ].filter(Boolean)),
    [needle, dashboardStack],
  );
  const visibleRecommendedTools = useMemo(
    () => tools.filter((tool) => !stackToolIds.has(tool.id)).slice(0, 6),
    [tools, stackToolIds],
  );

  const canSubmitWorkflowSelection = stage === 0 && chipSelections.length > 0;

  const submitWorkflowSelection = async () => {
    if (!canSubmitWorkflowSelection) return;
    await submitInput(chipSelections.join(', '));
    setChipSelections([]);
  };

  const handleStartOver = async () => {
    if (!agentRef.current || isTyping) return;
    setInputHint('');
    setChipSelections([]);
    await agentRef.current.handleUserInput('Start over');
  };

  const stagePercent = ((stage + 1) / 5) * 100;

  const stageTags = profileTagEntries(context);

  const shareUrl = useMemo(() => {
    if (!tools.length) return '';
    const profile = `${(context.userType || 'smb')}-${(context.role || 'general').toLowerCase().replace(/\s+/g, '-')}-${context.teamSizeNumeric || '5'}ppl`;
    const budget = encodeURIComponent((context.budget || '$50-$200/month').replace(/\s+/g, '-'));
    const result = tools.map((t) => t.slug).join('-');
    return `/find?profile=${profile}&budget=${budget}&result=${result}`;
  }, [tools, context]);

  return (
    <div className="min-h-screen pt-20 page-bg-shell page-bg-robot" data-testid="find-page">
      <Helmet>
        <title>Find My AI Tools | Wowrolla AI Advisor</title>
        <meta name="description" content="Conversational AI advisor that maps your pain points and recommends the right AI tools with transparent commission disclosure." />
      </Helmet>

      <div className="container mx-auto px-4 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" style={{ minHeight: 'calc(100vh - 120px)' }}>
          <section className="lg:col-span-3 xl:col-span-3 border" style={{ borderColor: 'var(--border-subtle)', background: '#050505' }}>
            <div className="p-4 border-b" style={{ borderColor: 'var(--border-subtle)', background: '#0B0C10' }}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 border flex items-center justify-center" style={{ borderColor: 'rgba(255,255,255,0.2)' }}>
                    <Bot size={16} style={{ color: '#FFE800' }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>AI Advisor</p>
                    <div className="flex items-center gap-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                      <Circle size={8} fill="#39FF14" color="#39FF14" /> online
                    </div>
                  </div>
                </div>
                <div className="text-xs px-2 py-1 border" style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-secondary)', fontFamily: 'JetBrains Mono, monospace' }}>
                  {liveToolCount} tools live · {suggestedCount} suggested
                </div>
              </div>

              <div className="mt-4">
                <div className="h-1" style={{ background: 'rgba(255,255,255,0.12)' }}>
                  <motion.div
                    className="h-full"
                    style={{ background: '#FFE800' }}
                    animate={{ width: `${stagePercent}%` }}
                    transition={{ duration: 0.35 }}
                    data-testid="finder-stage-progress"
                  />
                </div>
                <div className="grid grid-cols-5 gap-2 mt-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  {STAGE_NAMES.map((name, idx) => (
                    <div key={name} aria-current={idx === stage ? 'step' : undefined} data-testid={`finder-stage-${idx}`}>
                      {idx < stage ? '✓ ' : ''}{name}
                    </div>
                  ))}
                </div>
              </div>

              {stageTags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="finder-context-tags">
                  {stageTags.map((tag) => (
                    <span key={tag.key} className="text-[10px] px-2 py-0.5 border" style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-secondary)' }}>
                      {tag.key}: {tag.value}
                    </span>
                  ))}
                </div>
              )}

              {stage >= 3 && context.questionsRemaining !== undefined && (
                <div className="mt-2 text-xs px-2 py-1 border" style={{ borderColor: context.questionsRemaining <= 3 ? 'rgba(255,200,0,0.4)' : 'rgba(255,255,255,0.2)', color: context.questionsRemaining <= 3 ? '#FFD700' : 'var(--text-secondary)', fontFamily: 'JetBrains Mono, monospace', background: context.questionsRemaining <= 3 ? 'rgba(255,200,0,0.08)' : 'transparent' }}>
                  {context.questionsRemaining} questions remaining in session
                </div>
              )}

              {context.sessionLimitReached && (
                <div className="mt-2 text-xs px-2 py-1 border" style={{ borderColor: 'rgba(255,100,100,0.4)', color: '#FF6B6B', fontFamily: 'JetBrains Mono, monospace', background: 'rgba(255,100,100,0.08)' }}>
                  ⚠️ Session limit reached - start a new session or book a call
                </div>
              )}
            </div>

            <div ref={messageListRef} className="px-4 py-4 h-[54vh] lg:h-[58vh] overflow-y-auto" role="log" aria-live="polite" data-testid="finder-message-list">
              <AnimatePresence>
                {messageRows.map((msg) => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className={`mb-3 flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className="max-w-[84%] px-3 py-2 border"
                      style={{
                        background: msg.role === 'user' ? 'rgba(255,232,0,0.08)' : '#0B0C10',
                        borderColor: msg.role === 'user' ? 'rgba(255,232,0,0.25)' : 'rgba(255,255,255,0.1)',
                        color: msg.role === 'user' ? '#FFE800' : 'var(--text-primary)',
                      }}
                      data-testid={`finder-message-${msg.role}`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {isTyping && (
                <div className="mb-3 flex justify-start">
                  <div className="px-3 py-2 border" style={{ background: '#0B0C10', borderColor: 'rgba(255,255,255,0.1)' }}>
                    <TypingDots />
                  </div>
                </div>
              )}

              {visibleChips.length > 0 && (
                <div className="mb-3 border px-3 py-3" style={{ background: '#07080C', borderColor: 'rgba(255,255,255,0.12)' }} data-testid="finder-inline-options">
                  <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: '#A0A0A5' }}>
                    Next step options
                  </p>
                  <ConversationChips
                    chips={visibleChips}
                    onSelect={handleChipSelect}
                    multiSelect={stage === 0}
                    maxSelect={3}
                    selectedChips={chipSelections}
                    onDone={submitWorkflowSelection}
                    onEdit={() => setChipSelections([])}
                  />
                </div>
              )}

              {stage >= 3 && tools.length > 0 && (
                <div className="mt-3" data-testid="finder-recommendation-cards">
                  <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: '#A0A0A5' }}>
                    Recommended stack: up to 2 tools per selected workflow focus
                  </p>
                  {visibleRecommendedTools.length === 0 ? (
                    <div className="border p-3 text-xs" style={{ borderColor: 'rgba(255,255,255,0.12)', color: 'var(--text-muted)' }}>
                      All current recommendations are already in your My Tool Dashboard.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                      {visibleRecommendedTools.map((tool, idx) => (
                        <div
                          key={tool.id || tool.slug || idx}
                          className="border p-3"
                          style={{ background: '#0B0C10', borderColor: 'rgba(255,255,255,0.12)' }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>
                              {tool.category_name || tool.capability_label || 'General'}
                            </p>
                            <span className="text-[10px]" style={{ color: '#FFE800' }}>
                              {Number.isFinite(Number(tool.fitScore)) ? `${Math.max(1, Math.min(100, Number(tool.fitScore)))}% fit` : `${Number.isFinite(Number(tool.finderScore)) ? Math.max(1, Math.min(100, Math.round(Number(tool.finderScore) * 10))) : 70}% fit`}
                            </span>
                          </div>
                          <h4 className="text-sm font-semibold mt-1" style={{ color: 'var(--text-primary)' }}>{tool.name}</h4>
                          <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>{tool.tagline || tool.tool_tagline || 'Fit-matched for your context'}</p>

                          <div className="mt-2 flex flex-wrap gap-1">
                            <button
                              type="button"
                              onClick={() => addToNeedle(tool)}
                              className="px-2 py-1 text-[10px] font-semibold"
                              style={{ background: '#FFE800', color: '#000' }}
                            >
                              Add
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const ids = [tool.id, ...needle.map((n) => n.id).filter((id) => id !== tool.id)].slice(0, 3);
                                if (ids.length >= 2) navigate(`/compare?tools=${ids.join(',')}`);
                              }}
                              className="px-2 py-1 text-[10px] border"
                              style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-secondary)' }}
                            >
                              Compare
                            </button>
                            {tool.slug && (
                              <button
                                type="button"
                                onClick={() => navigate(`/tools/${tool.slug}`)}
                                className="px-2 py-1 text-[10px] border"
                                style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-secondary)' }}
                              >
                                Details
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {showConsulting && (
                <div className="mt-4">
                  <ConsultingTriggerCard
                    complexityScore={consultingScore}
                    needle={needle}
                    userProfile={context}
                    onBook={(url) => {
                      window.dispatchEvent(new CustomEvent('wowrolla-ga4', { detail: { event: 'find_consulting_booked' } }));
                      navigate(url);
                    }}
                    onSkip={() => {
                      setShowConsulting(false);
                      setMessages((prev) => [...prev, {
                        id: `skip-${Date.now()}`,
                        role: 'assistant',
                        content: "No problem - here's your setup guide for each tool.",
                        createdAt: new Date().toISOString(),
                      }]);
                    }}
                    onExplain={() => submitInput("Tell me what's involved")}
                  />
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="px-4 py-3 border-t sticky bottom-0" style={{ borderColor: 'var(--border-subtle)', background: '#050505' }}>
              <div className="mt-1">
                <textarea
                  ref={textareaRef}
                  value={inputText}
                  onChange={(e) => {
                    setInputText(e.target.value);
                    if (inputHint) {
                      setInputHint('');
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      submitInput(inputText);
                    }
                  }}
                  rows={2}
                  placeholder="Type your answer or ask anything (Enter to send, Shift+Enter newline)"
                  className="w-full px-3 py-2 border resize-none"
                  style={{
                    borderColor: 'rgba(255,255,255,0.15)',
                    background: '#0B0C10',
                    color: 'var(--text-primary)',
                  }}
                  data-testid="finder-input"
                />
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={() => submitInput(inputText)}
                    className="px-4 py-2 text-sm font-semibold rounded-none inline-flex items-center gap-1"
                    style={{ background: '#FFE800', color: '#000', opacity: isTyping ? 0.6 : 1 }}
                    disabled={isTyping}
                    data-testid="finder-send-button"
                  >
                    Send <Send size={13} />
                  </button>
                </div>
                {inputHint && (
                  <p className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>{inputHint}</p>
                )}
              </div>
            </div>
          </section>

          <aside className="lg:col-span-2 xl:col-span-2 lg:sticky lg:top-24 self-start">
            <div className="lg:hidden mb-2">
              <button
                type="button"
                onClick={() => setIsMobileContextOpen((v) => !v)}
                className="w-full px-3 py-2 border text-xs font-semibold flex items-center justify-between"
                style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)', background: '#0B0C10' }}
                data-testid="finder-context-toggle"
              >
                Context Panel <ChevronDown size={14} />
              </button>
            </div>

            <div className={`${isMobileContextOpen ? 'block' : 'hidden'} lg:block border p-4 lg:max-h-[calc(100vh-7.5rem)] lg:overflow-y-auto`} style={{ borderColor: 'var(--border-subtle)', background: '#0B0C10' }}>
              <h2 className="text-sm font-bold mb-3" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>Live Context</h2>

              <div className="space-y-2 text-xs mb-4" data-testid="finder-profile-summary">
                <div style={{ color: 'var(--text-secondary)' }}><span style={{ color: 'var(--text-muted)' }}>Role:</span> {context.role || 'Pending'}</div>
                <div style={{ color: 'var(--text-secondary)' }}><span style={{ color: 'var(--text-muted)' }}>Team:</span> {context.teamSize || 'Pending'}</div>
                <div style={{ color: 'var(--text-secondary)' }}><span style={{ color: 'var(--text-muted)' }}>Budget:</span> {context.budget || 'Pending'}</div>
                <div style={{ color: 'var(--text-secondary)' }}><span style={{ color: 'var(--text-muted)' }}>Pain:</span> {context.painPoint || 'Pending'}</div>
              </div>

              <div className="border p-3 mb-4" style={{ borderColor: 'var(--border-subtle)' }}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>My Tool Dashboard (Pinned)</p>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={clearPinnedDashboard}
                      className="text-[10px] px-2 py-1 border"
                      style={{ borderColor: 'rgba(255,120,120,0.35)', color: '#FF8F8F' }}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={loadDashboardStack}
                      className="text-[10px] px-2 py-1 border"
                      style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-secondary)' }}
                    >
                      Refresh
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5" data-testid="finder-needle-preview">
                  {stackPreviewItems.length === 0 && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No tools added yet.</p>}
                  {stackPreviewItems.map((item, idx) => (
                    <div key={item.id || item.tool_id || `${item.name || 'tool'}-${idx}`} className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item.color || '#FFE800' }} />
                      <span className="truncate flex-1">{item.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFromPinnedDashboard(item.tool_id || item.id || item.toolId)}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 border flex-shrink-0"
                        style={{ borderColor: 'rgba(255,120,120,0.35)', color: '#FF8F8F' }}
                        aria-label={`Remove ${item.name}`}
                        title={`Remove ${item.name}`}
                      >
                        <Trash2 size={10} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1">
                  <Link to="/my-stack" className="text-[10px] px-2 py-1 border text-center" style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-secondary)' }}>
                    Open Stack
                  </Link>
                  <Link to="/dashboard/stack" className="text-[10px] px-2 py-1 border text-center" style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-secondary)' }}>
                    Full Dashboard
                  </Link>
                </div>
              </div>

              <div className="border p-3 mb-4" style={{ borderColor: 'var(--border-subtle)' }}>
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Recommended Queue</p>
                <div className="space-y-1.5" data-testid="finder-live-recommended-preview">
                  {visibleRecommendedTools.length === 0 && (
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No pending recommendations.</p>
                  )}
                  {visibleRecommendedTools.slice(0, 6).map((item, idx) => (
                    <div key={item.id || `${item.name || 'tool'}-${idx}`} className="flex items-center justify-between gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      <span className="truncate">{item.name}</span>
                      <span style={{ color: '#FFE800' }}>
                        {Number.isFinite(Number(item.fitScore)) ? `${Math.max(1, Math.min(100, Number(item.fitScore)))}%` : `${Number.isFinite(Number(item.finderScore)) ? Math.max(1, Math.min(100, Math.round(Number(item.finderScore) * 10))) : 70}%`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-[10px] mb-4" style={{ color: 'var(--text-muted)' }}>
                Answers remembered for 30 days if you create an account.
              </p>

              <div className="space-y-2 text-xs">
                <Link to="/compare" className="block px-3 py-2 border" style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)' }} data-testid="finder-quick-compare">/compare</Link>
                <Link to="/my-stack/build" className="block px-3 py-2 border" style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)' }} data-testid="finder-quick-stack-builder">/stack-builder</Link>
                <Link to="/consulting" className="block px-3 py-2 border" style={{ borderColor: 'rgba(57,255,20,0.3)', color: '#39FF14' }} data-testid="finder-quick-consulting">/consulting</Link>
              </div>

              <div className="mt-4 pt-3 border-t" style={{ borderColor: 'var(--border-subtle)' }} data-testid="finder-persistent-actions">
                <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                  Quick actions
                </p>
                <div className="grid grid-cols-2 gap-1">
                  <button
                    type="button"
                    onClick={handleStartOver}
                    className="text-[10px] px-2 py-1 border"
                    style={{ borderColor: 'rgba(255,232,0,0.55)', color: '#FFE800', background: 'rgba(255,232,0,0.08)' }}
                    data-testid="finder-action-startover"
                    disabled={isTyping}
                  >
                    Start over
                  </button>
                  <Link
                    to="/search"
                    className="text-[10px] px-2 py-1 border text-center"
                    style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-secondary)' }}
                    data-testid="finder-action-tools"
                  >
                    Tools
                  </Link>
                  <Link
                    to="/categories"
                    className="text-[10px] px-2 py-1 border text-center"
                    style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-secondary)' }}
                    data-testid="finder-action-categories"
                  >
                    Categories
                  </Link>
                  <Link
                    to="/login"
                    className="text-[10px] px-2 py-1 border text-center"
                    style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-secondary)' }}
                    data-testid="finder-action-login"
                  >
                    Login
                  </Link>
                </div>
              </div>

              {shareUrl && (
                <div className="mt-4 pt-3 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                  <p className="text-[10px] mb-2" style={{ color: 'var(--text-muted)' }}>Shareable run URL</p>
                  <div className="text-[10px]" style={{ color: '#FFE800', fontFamily: 'JetBrains Mono, monospace', wordBreak: 'break-all' }}>
                    {shareUrl}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>


        <AnimatePresence>
          {showRestorePrompt && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[120] flex items-center justify-center px-4"
              style={{ background: 'rgba(0,0,0,0.72)' }}
            >
              <div className="max-w-md w-full border p-5" style={{ background: '#0B0C10', borderColor: 'var(--border-subtle)' }}>
                <h3 className="text-base font-bold mb-2" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>Welcome back</h3>
                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                  Continue where you left off?
                </p>
                <div className="flex gap-2">
                  <button onClick={handleRestoreYes} className="px-3 py-2 text-xs font-semibold rounded-none" style={{ background: '#FFE800', color: '#000' }} data-testid="finder-restore-yes">
                    Yes
                  </button>
                  <button onClick={handleRestoreNo} className="px-3 py-2 text-xs border rounded-none" style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-secondary)' }} data-testid="finder-restore-no">
                    Start fresh
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

